import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { auth, db } from './firebase';
import { GoogleAuthProvider, signInWithPopup, signOut, createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { addDoc, collection, serverTimestamp, doc, getDoc, setDoc, query, where, getDocs } from 'firebase/firestore';
import { Button, Container, Typography, Box, Paper, AppBar, Toolbar, IconButton, Drawer, useMediaQuery, useTheme, TextField, Divider, Alert, CircularProgress } from '@mui/material';
import { Add as AddIcon, ExitToApp as LogoutIcon, Menu as MenuIcon, Google as GoogleIcon, Email as EmailIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import Chat from './components/Chat';
import ConversationList from './components/ConversationList';
import LanguageSwitcher from './components/LanguageSwitcher';
import OnboardingWizard from './components/OnboardingWizard';
import { normalizeLanguage } from './utils/languageUtils';
import { UserProfileModal } from './components/UserProfileModal';
import PublicPetProfileRoute from './components/PublicPetProfileRoute';
import LandingPage from './components/LandingPage';
import IntakeFormRoute from './components/IntakeFormRoute';

import { createThread } from './services/openai';
import { hasCompletedOnboarding } from './services/onboardingService';
import { analyticsService } from './services/analyticsService';

// Main App component that handles routing
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

// App content component that contains the main logic
function AppContent() {
  const { t } = useTranslation();
  const location = useLocation();
  const isPublicRoute = location.pathname.startsWith('/intake/') || location.pathname.startsWith('/pet/');
  console.log('INTAKE_FIX_V2');
  const [user, setUser] = useState<any>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);

  // New state for roadmap artifact view
  const [showRoadmapView, setShowRoadmapView] = useState(false);
  const [currentRoadmap, setCurrentRoadmap] = useState<any>(null);

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(false);

  // User profile modal state
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  // Onboarding trigger state
  const [triggerOnboarding, setTriggerOnboarding] = useState(false);

  // Landing page state
  const [showLandingPage, setShowLandingPage] = useState(() => {
    // Check if user has seen landing page before
    const hasSeenLanding = localStorage.getItem('hasSeenLanding');
    console.log('🎬 Landing page check:', { hasSeenLanding, willShow: !hasSeenLanding });
    // TEMPORARY: Always show landing page for testing (remove this in production)
    return true; // Changed from: !hasSeenLanding
  });



  // Authentication form state
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Listen for custom event to open profile modal
  useEffect(() => {
    const handleOpenUserProfile = () => {
      setProfileModalOpen(true);
    };

    window.addEventListener('openUserProfile', handleOpenUserProfile);
    return () => {
      window.removeEventListener('openUserProfile', handleOpenUserProfile);
    };
  }, []);

  // Listen for language changes and update user object
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      if (user) {
        setUser({
          ...user,
          language: normalizeLanguage(lng)
        });
      }
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [user, i18n]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      console.log('🔄 Auth state changed:', user?.uid || 'No user');
      setUser(user);
      if (user) {
        console.log('✅ User authenticated:', user.email, user.uid);

        // Fetch user data from Firestore to get the full photo URL and language
        try {
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            // Update user object with Firestore data
            setUser({
              ...user,
              displayName: userData.displayName || user.displayName,
              photoURL: userData.photoURL || user.photoURL,
              language: normalizeLanguage(userData.language || 'en') // Get language from Firestore
            });
          } else {
            // User document doesn't exist yet, create it
            console.log('User document not found, creating new one');
            // Detect language from i18next or default to 'en'
            const detectedLanguage = normalizeLanguage(i18n.language || 'en');
            await setDoc(userRef, {
              displayName: user.displayName || 'Pet Owner',
              photoURL: user.photoURL || null,
              email: user.email || '',
              language: detectedLanguage,
              createdAt: new Date(),
              updatedAt: new Date()
            });
            // Update user object with detected language
            setUser({
              ...user,
              displayName: user.displayName || 'Pet Owner',
              photoURL: user.photoURL || null,
              language: detectedLanguage
            });
          }
        } catch (error) {
          console.error('Error fetching user data from Firestore:', error);
        }

        // Check onboarding status for new users
        checkOnboardingStatus(user.uid);

        // Temporarily disable roadmap checking to avoid permission issues
        // checkForCompletedRoadmaps(user.uid);
        // Set up periodic checking for new completions
        // const interval = setInterval(() => {
        //   checkForCompletedRoadmaps(user.uid);
        // }, 3000); // Check every 3 seconds

        // return () => clearInterval(interval);
      } else {
        console.log('❌ No authenticated user');
        setShowOnboarding(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Removed unused checkForCompletedRoadmaps function

  const checkOnboardingStatus = async (userId: string) => {
    try {
      setIsCheckingOnboarding(true);
      console.log('🔍 Checking onboarding status for user:', userId);

      const hasCompleted = await hasCompletedOnboarding(userId);
      console.log('🔍 Onboarding status:', hasCompleted ? 'completed' : 'not completed');

      if (!hasCompleted) {
        console.log('🎯 User needs onboarding - showing onboarding wizard');
        setShowOnboarding(true);
      } else {
        console.log('✅ User has completed onboarding - showing main app');
        setShowOnboarding(false);
      }
    } catch (error) {
      console.error('❌ Error checking onboarding status:', error);
      // Default to showing onboarding if we can't check
      console.log('⚠️ Defaulting to show onboarding due to error');
      setShowOnboarding(true);
    } finally {
      setIsCheckingOnboarding(false);
    }
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();

    // Add additional OAuth scopes if needed
    provider.addScope('email');
    provider.addScope('profile');

    try {
      setAuthLoading(true);
      setAuthError(null);

      console.log('🔄 Attempting Google Sign-In...');
      console.log('🔧 Firebase Auth State:', {
        isInitialized: !!auth,
        currentUser: auth?.currentUser?.uid || 'none',
        app: !!auth?.app,
        authDomain: auth?.app?.options?.authDomain,
        projectId: auth?.app?.options?.projectId
      });

      // Additional debugging for 403 errors
      console.log('🔍 Pre-signin validation:', {
        currentUrl: window.location.href,
        origin: window.location.origin,
        hostname: window.location.hostname,
        protocol: window.location.protocol
      });

      const result = await signInWithPopup(auth, provider);
      console.log('✅ Google Sign-In successful:', {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName
      });

      // ✅ Track user signup (check if new user)
      try {
        const userRef = doc(db, 'users', result.user.uid);
        const userSnap = await getDoc(userRef);
        const isNewUser = !userSnap.exists();

        if (isNewUser) {
          await analyticsService.trackSignup(
            result.user.uid,
            'google',
            i18n.language
          );
          console.log('📊 Analytics: New Google user signup tracked');
        }
      } catch (analyticsError) {
        console.error('Analytics error (non-blocking):', analyticsError);
      }

    } catch (error: any) {
      console.error('❌ Google Sign-In Error:', error);

      let errorMessage = 'Google Sign-In failed. Please try again.';

      // Enhanced error handling for Google Sign-In
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Sign-in was cancelled. Please try again.';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Popup was blocked by your browser. Please allow popups and try again.';
          break;
        case 'auth/cancelled-popup-request':
          errorMessage = 'Sign-in was cancelled. Please try again.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Google Sign-In is not enabled. Please contact support.';
          break;
        case 'auth/unauthorized-domain':
          errorMessage = 'This domain is not authorized for Google Sign-In.';
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'An account already exists with this email using a different sign-in method.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your internet connection.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many requests. Please try again later.';
          break;
        default:
          console.error('Unhandled Google Sign-In error:', {
            code: error.code,
            message: error.message,
            stack: error.stack
          });
          errorMessage = `Google Sign-In failed: ${error.message || 'Unknown error'}`;
      }

      setAuthError(errorMessage);
    } finally {
      setAuthLoading(false);
    }
  };

  const signUpWithEmail = async () => {
    if (password !== confirmPassword) {
      setAuthError(t('auth.passwordMismatch'));
      return;
    }

    if (password.length < 6) {
      setAuthError(t('auth.passwordTooShort'));
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setAuthError(t('auth.invalidEmail'));
      return;
    }

    try {
      setAuthLoading(true);
      setAuthError(null);

      console.log('🔄 Attempting to create user account...', {
        email: email,
        hasPassword: !!password,
        displayName: displayName
      });

      // Check if Firebase auth is properly initialized
      if (!auth || (typeof auth === 'object' && Object.keys(auth).length === 0)) {
        throw new Error('Firebase authentication is not properly initialized. Please check your configuration.');
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      console.log('✅ User account created successfully:', {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        emailVerified: userCredential.user.emailVerified
      });

      // ✅ Track user signup
      try {
        await analyticsService.trackSignup(
          userCredential.user.uid,
          'email',
          i18n.language
        );
        console.log('📊 Analytics: User signup tracked');
      } catch (analyticsError) {
        console.error('Analytics error (non-blocking):', analyticsError);
      }

      // Update the user's display name
      if (displayName.trim()) {
        try {
          await updateProfile(userCredential.user, {
            displayName: displayName.trim()
          });
          console.log('✅ Display name updated successfully');
        } catch (profileError) {
          console.warn('⚠️ Failed to update display name:', profileError);
          // Don't fail the entire registration for this
        }
      }

      // Clear form
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setDisplayName('');

      console.log('🎉 Registration completed successfully');

    } catch (error: any) {
      console.error('❌ Error creating account:', error);
      let errorMessage = t('auth.createAccountError');

      // Enhanced error handling with more specific messages
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = t('auth.emailInUse');
          break;
        case 'auth/invalid-email':
          errorMessage = t('auth.invalidEmail');
          break;
        case 'auth/weak-password':
          errorMessage = t('auth.weakPassword');
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Email/password authentication is not enabled. Please contact support.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your internet connection and try again.';
          break;
        case 'auth/too-many-requests':
          errorMessage = t('auth.tooManyRequests');
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Invalid credentials. Please check your information and try again.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled. Please contact support.';
          break;
        default:
          // Log the full error for debugging
          console.error('Unhandled auth error:', {
            code: error.code,
            message: error.message,
            stack: error.stack
          });
          errorMessage = `Registration failed: ${error.message || 'Unknown error occurred'}`;
      }

      setAuthError(errorMessage);
    } finally {
      setAuthLoading(false);
    }
  };

  const signInWithEmail = async () => {
    try {
      setAuthLoading(true);
      setAuthError(null);

      await signInWithEmailAndPassword(auth, email, password);

      // Clear form
      setEmail('');
      setPassword('');
    } catch (error: any) {
      console.error('Error signing in:', error);
      let errorMessage = t('auth.signInError');

      if (error.code === 'auth/user-not-found') {
        errorMessage = t('auth.userNotFound');
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = t('auth.wrongPassword');
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = t('auth.invalidEmail');
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = t('auth.tooManyRequests');
      }

      setAuthError(errorMessage);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !password.trim()) {
      setAuthError(t('auth.fillAllFields'));
      return;
    }

    if (authMode === 'signup') {
      await signUpWithEmail();
    } else {
      await signInWithEmail();
    }
  };

  const toggleAuthMode = () => {
    setAuthMode(authMode === 'signin' ? 'signup' : 'signin');
    setAuthError(null);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setDisplayName('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setSelectedConversationId(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSelectConversation = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    // Reset roadmap view when switching conversations
    setShowRoadmapView(false);
    setCurrentRoadmap(null);
    if (isMobile) {
      setMobileOpen(false); // Close drawer on mobile when conversation is selected
    }
  };

  const handleNewChat = async () => {
    console.log('🔄 handleNewChat called');
    console.log('Current user:', auth.currentUser?.uid || 'No user');
    console.log('User state:', user?.uid || 'No user state');

    // Check both Firebase auth and local state
    if (!auth.currentUser || !user) {
      console.error('❌ No authenticated user found');
      console.error('Firebase auth user:', auth.currentUser?.uid || 'none');
      console.error('Local user state:', user?.uid || 'none');
      setAuthError('Please sign in before creating a conversation');
      return;
    }

    // Double check user ID matches
    if (auth.currentUser.uid !== user.uid) {
      console.error('🚫 User ID mismatch between Firebase and local state');
      console.error('Firebase UID:', auth.currentUser.uid);
      console.error('Local UID:', user.uid);
      setAuthError('Authentication state mismatch. Please refresh the page.');
      return;
    }

    setIsCreatingConversation(true);
    try {
      console.log('🔄 Creating OpenAI thread...');
      const threadId = await createThread();
      console.log('✅ Thread created:', threadId);

      // Get localized text for new conversation title
      const getLocalizedText = (key: string) => {
        const texts: Record<string, string> = {
          newConversation: 'New Conversation',
          newConversationZh: '新对话',
          newConversationEs: 'Nueva Conversación',
          newConversationFr: 'Nouvelle Conversation'
        };
        return texts[key] || texts.newConversation;
      };

      const newConversation = {
        title: getLocalizedText('newConversation'),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        userId: user.uid,
        threadId: threadId,
      };

      console.log('🔄 Creating Firestore document...', newConversation);
      const docRef = await addDoc(collection(db, 'conversations'), newConversation);
      console.log('✅ Conversation created:', docRef.id);

      // ✅ Track conversation creation
      try {
        await analyticsService.trackEvent({
          userId: user.uid,
          eventType: 'conversation_created',
          timestamp: serverTimestamp() as any,
          language: i18n.language as any,
          metadata: {
            conversationId: docRef.id,
            threadId: threadId
          }
        });
        console.log('📊 Analytics: Conversation creation tracked');
      } catch (analyticsError) {
        console.error('Analytics error (non-blocking):', analyticsError);
      }

      setSelectedConversationId(docRef.id);

      if (isMobile) {
        setMobileOpen(false);
      }

      console.log('✅ Conversation creation completed successfully');
    } catch (error) {
      console.error('❌ Error creating conversation:', error);

      // Enhanced error handling for Firestore permission issues
      if (error instanceof Error) {
        if (error.message.includes('permission-denied')) {
          console.error('🚫 Permission denied - User may not be properly authenticated');
          console.error('User ID:', auth.currentUser?.uid);
          setAuthError('Authentication error. Please sign out and sign in again.');
        } else if (error.message.includes('Missing or insufficient permissions')) {
          console.error('🚫 Insufficient permissions - Check Firestore rules');
          console.error('User ID:', auth.currentUser?.uid);
          setAuthError('Permission error. Please contact support.');
        } else {
          setAuthError(`Error creating conversation: ${error.message}`);
        }
      }
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // New function to handle roadmap view toggle
  const handleRoadmapViewToggle = (roadmap: any) => {
    if (showRoadmapView && currentRoadmap?.id === roadmap?.id) {
      // If clicking the same roadmap, close the view
      setShowRoadmapView(false);
      setCurrentRoadmap(null);
    } else {
      // Open roadmap view with new roadmap
      setCurrentRoadmap(roadmap);
      setShowRoadmapView(true);
    }
  };

  // Direct function to return to chat view without animation delay
  const handleReturnToChat = () => {
    setShowRoadmapView(false);
    setCurrentRoadmap(null);
  };

  // Handle user profile updates
  const handleUserUpdate = (updates: { displayName?: string; photoURL?: string }) => {
    if (user) {
      setUser({
        ...user,
        displayName: updates.displayName || user.displayName,
        photoURL: updates.photoURL !== undefined ? updates.photoURL : user.photoURL
      });
    }
  };

  // Handle profile modal open/close
  const handleProfileModalOpen = () => {
    setProfileModalOpen(true);
  };

  const handleProfileModalClose = () => {
    setProfileModalOpen(false);
  };

  // Handle starting onboarding from profile modal
  const handleStartOnboardingFromProfile = () => {
    setTriggerOnboarding(true);
    setShowOnboarding(true);
  };




  // Handle onboarding completion
  const handleOnboardingComplete = async (petProfile: any) => {
    console.log('🎉 Onboarding completed for pet:', petProfile.name);
    setShowOnboarding(false);
    setTriggerOnboarding(false); // Reset trigger state

    // Only create a welcome conversation for truly new users (no existing conversations)
    try {
      // Check if user already has existing conversations
      const conversationsRef = collection(db, 'conversations');
      const userConversationsQuery = query(conversationsRef, where('userId', '==', user.uid));
      const userConversationsSnapshot = await getDocs(userConversationsQuery);

      if (userConversationsSnapshot.empty) {
        // This is a new user with no existing conversations, create welcome chat
        console.log('🔄 Creating welcome conversation for new user...');
        await handleNewChat();
        console.log('✅ Welcome conversation created');
      } else {
        // This is an existing user adding another pet, don't create a new chat
        console.log('ℹ️ Existing user adding pet, skipping welcome conversation creation');
      }
    } catch (error) {
      console.log('⚠️ Could not check/create welcome conversation, but onboarding is complete');
    }
  };

  // Handle onboarding errors
  const handleOnboardingError = (error: string) => {
    console.error('❌ Onboarding error:', error);
    // You could show a snackbar or alert here
  };

  // Handle onboarding cancellation
  const handleOnboardingCancel = () => {
    console.log('🚫 Onboarding cancelled by user');
    setShowOnboarding(false);
    setTriggerOnboarding(false); // Reset trigger state
  };

  // Handle landing page "Get Started" button
  const handleGetStarted = () => {
    setShowLandingPage(false);
    localStorage.setItem('hasSeenLanding', 'true');
  };

  // Show landing page for non-authenticated users who haven't seen it
  if (!user && showLandingPage && !isPublicRoute) {
    console.log('✅ Showing landing page');
    return <LandingPage onGetStarted={handleGetStarted} />;
  }

  console.log('🔍 Landing page check:', { user: !!user, showLandingPage });

  if (!user && !isPublicRoute) {
    return (
      <Container maxWidth="sm">
        <Box sx={{
          mt: 4,
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '90vh'
        }} style={{ padding: '32px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <img
              src="/PetSnap_logo_whole_transparent.png"
              alt="PetSnap - Pet Care Assistant Logo"
              style={{
                height: '120px',
                width: 'auto',
                marginBottom: '16px',
                filter: 'drop-shadow(0 4px 12px rgba(244, 162, 97, 0.3))',
                animation: 'softPulse 3s infinite ease-in-out'
              }}
            />
          </Box>
          <Typography variant="body1" component="p" gutterBottom sx={{ mb: 4, textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.95rem', fontWeight: 500 }}>
            {t('appSubtitle')}
          </Typography>

          <Paper elevation={0} sx={{ p: 4, width: '100%', maxWidth: 400 }} className="petsnap-card">
            <Typography variant="h5" component="h2" gutterBottom textAlign="center" fontWeight={700} color="primary" sx={{ mb: 3 }}>
              {authMode === 'signin' ? t('signIn') : t('createAccount')}
            </Typography>

            {authError && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', backgroundColor: '#FEF2F2', color: 'var(--petsnap-terracotta)', border: '1px solid rgba(231, 111, 81, 0.2)' }}>
                {authError}
              </Alert>
            )}

            <Box component="form" onSubmit={handleAuthSubmit} sx={{ mt: 2 }}>
              {authMode === 'signup' && (
                <TextField
                  fullWidth
                  label={t('displayName')}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  margin="normal"
                  variant="outlined"
                />
              )}

              <TextField
                fullWidth
                required
                label={t('emailAddress')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                variant="outlined"
                autoComplete="email"
              />

              <TextField
                fullWidth
                required
                label={t('password')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                variant="outlined"
                autoComplete={authMode === 'signin' ? 'current-password' : 'new-password'}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />

              {authMode === 'signup' && (
                <TextField
                  fullWidth
                  required
                  label={t('confirmPassword')}
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  variant="outlined"
                  autoComplete="new-password"
                />
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={authLoading}
                startIcon={authLoading ? <CircularProgress size={20} /> : <EmailIcon />}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {authLoading
                  ? (authMode === 'signin' ? t('signingIn') : t('creatingAccount'))
                  : (authMode === 'signin' ? t('signIn') : t('createAccount'))
                }
              </Button>
            </Box>

            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('or')}
              </Typography>
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              onClick={signInWithGoogle}
              disabled={authLoading}
              startIcon={authLoading ? <CircularProgress size={20} /> : <GoogleIcon />}
              sx={{ py: 1.5, mb: 2 }}
            >
              {authLoading ? t('signingIn') : t('signInWithGoogle')}
            </Button>

            <Box textAlign="center">
              <Button
                variant="text"
                onClick={toggleAuthMode}
                disabled={authLoading}
              >
                {authMode === 'signin'
                  ? t('dontHaveAccount')
                  : t('alreadyHaveAccount')
                }
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  const drawerWidth = 350;

  const drawer = (
    <Box sx={{
      height: '100%', // Use parent height
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden' // Prevent overflow
    }}>
      <ConversationList
        onSelectConversation={handleSelectConversation}
        selectedConversationId={selectedConversationId}
        user={user}
      />
    </Box>
  );

  // Show loading while checking onboarding status
  if (isCheckingOnboarding) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}
      >
        <CircularProgress size={64} sx={{ mb: 3 }} />
        <Typography variant="h6" color="text.secondary">
          {t('onboarding.initializing', 'Setting up your experience...')}
        </Typography>
      </Box>
    );
  }

  // Show onboarding wizard for new users or when triggered from profile
  return (
    <Routes>
      {/* Public Pet Profile Route */}
      <Route path="/pet/:shareLinkId" element={<PublicPetProfileRoute />} />
      <Route path="/intake/:professionalId" element={<IntakeFormRoute />} />

      {/* Main App Route (Catches all other paths) */}
      <Route path="/*" element={
        showOnboarding ? (
          <Box sx={{ height: '100vh', overflow: 'auto' }}>
            <OnboardingWizard
              userId={user?.uid || ''}
              userLanguage={user?.language || i18n.language || 'en'}
              onComplete={handleOnboardingComplete}
              onCancel={handleOnboardingCancel}
              onError={handleOnboardingError}
              skipWelcomeStep={triggerOnboarding}
            />
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Top App Bar */}
            <AppBar position="static" elevation={1} sx={{ borderRadius: 0 }}>
              <Toolbar>
                {isMobile && (
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{ mr: 2 }}
                  >
                    <MenuIcon />
                  </IconButton>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setSelectedConversationId(null);
                    setShowRoadmapView(false);
                    setCurrentRoadmap(null);
                  }}
                >
                  <img
                    src="/PetSnap_logo_whole_transparent.png"
                    alt="PetSnap - Pet Care Assistant Logo"
                    style={{
                      height: '40px',
                      width: 'auto',
                      marginRight: '12px',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
                  {t('welcome')}, {user?.displayName}
                </Typography>
                <LanguageSwitcher userId={user?.uid} />
                <IconButton color="inherit" onClick={handleSignOut} title="Sign Out">
                  <LogoutIcon />
                </IconButton>
              </Toolbar>
            </AppBar>

            {/* Main Content Area */}
            <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
              {/* Navigation Drawer */}
              <Box
                component="nav"
                sx={{
                  width: { md: showRoadmapView ? 0 : drawerWidth },
                  flexShrink: { md: 0 },
                  transition: 'all 0.3s ease-in-out',
                  overflow: 'hidden',
                  height: '100%'
                }}
              >
                {/* Mobile drawer */}
                <Drawer
                  variant="temporary"
                  open={mobileOpen}
                  onClose={handleDrawerToggle}
                  ModalProps={{
                    keepMounted: true, // Better open performance on mobile.
                  }}
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                  }}
                >
                  {drawer}
                </Drawer>

                {/* Desktop drawer */}
                <Drawer
                  variant="permanent"
                  sx={{
                    display: { xs: 'none', md: showRoadmapView ? 'none' : 'block' },
                    height: '100%',
                    '& .MuiDrawer-paper': {
                      boxSizing: 'border-box',
                      width: drawerWidth,
                      height: '100%',
                      maxHeight: '100%',
                      position: 'relative',
                      borderRight: 1,
                      borderColor: 'divider',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden'
                    },
                  }}
                  open
                >
                  {drawer}
                </Drawer>
              </Box>

              {/* Right Panel - Main Content Area */}
              <Box sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                width: { md: showRoadmapView ? '100%' : `calc(100% - ${drawerWidth}px)` },
                transition: 'all 0.3s ease-in-out'
              }}>
                {selectedConversationId ? (
                  <Chat
                    conversationId={selectedConversationId}
                    showRoadmapView={showRoadmapView}
                    currentRoadmap={currentRoadmap}
                    onRoadmapViewToggle={handleRoadmapViewToggle}
                    onReturnToChat={handleReturnToChat}
                    onNavigateToHome={() => {
                      setSelectedConversationId(null);
                      setShowRoadmapView(false);
                      setCurrentRoadmap(null);
                    }}
                    user={user}
                  />
                ) : (
                  <Box sx={{
                    flexGrow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    p: 4,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h4" gutterBottom color="primary">
                      {t('welcomeTitle')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600 }}>
                      {t('welcomeDescription')}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleNewChat}
                      disabled={isCreatingConversation || !user}
                      size="large"
                      sx={{ mb: 2 }}
                    >
                      {isCreatingConversation ? t('creating') : t('startNewConversation')}
                    </Button>


                  </Box>
                )}
              </Box>
            </Box>

            {/* User Profile Modal */}
            <UserProfileModal
              open={profileModalOpen}
              onClose={handleProfileModalClose}
              userId={user?.uid || ''}
              userLanguage={user?.language || i18n.language || 'en'}
              userDisplayName={user?.displayName || 'Pet Owner'}
              userEmail={user?.email || 'pet@example.com'}
              userPhotoURL={user?.photoURL}
              onUserUpdate={handleUserUpdate}
              onStartOnboarding={handleStartOnboardingFromProfile}
            />
          </Box>
        )
      } />
    </Routes>
  );
}

export default App;
